import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { Origin, TabsList } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetProjectDetailsResponse as GetPublicProjectDetailsResponse } from "../../../../service/publicquery";
import { GetProjectDetailsResponse } from "../../../../service/query";
import { useAuth } from "../../../../useAuth";
import {
  getDashboardRoute,
  getProjectAccessRoute,
  getProjectActivitiesRoute,
  getProjectAuditHistoryRoute,
  getProjectDetailsByUuid,
  getProjectDetailsRoute,
  getProjectDocumentsRoute,
  getProjectInventoryRoute,
  getProjectLocationRoute,
  getProjectMarketingSettingsRoute,
  getProjectsRoute,
  getProjectTasksRoute,
  getSettingsAuditHistoryRoute,
  getWorkflowTemplatesRoute,
} from "../../../../utils/routes";
import { ProjectContext } from "../../../shared/projects/project/ProjectContext";

interface UseProjectReturnData {
  projectDetails: GetProjectDetailsResponse | undefined;
  publicProjectDetails: GetPublicProjectDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
}

export const useProject = (): UseProjectReturnData => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { currentUserType } = useAuth();
  const { projectDetails, publicProjectDetails, isPreAccess } = useContext(ProjectContext);
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const origin = searchParams.get("origin");

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    switch (origin) {
      case Origin.ProjectAuditPage:
      case Origin.OrganisationAuditPage:
        backButtonText = "Back to audit";
        break;
      case Origin.WorkflowsPage:
        backButtonText = "Back to workflows";
        break;
      case Origin.DashboardPage:
        backButtonText = "Back to dashboard";
        break;
      default:
        backButtonText = "Back to projects";
        break;
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    switch (origin) {
      case Origin.ProjectAuditPage:
        if (projectDetails?.uuid) {
          navigate(getProjectAuditHistoryRoute(projectDetails.uuid, currentUserType));
        }
        break;
      case Origin.OrganisationAuditPage:
        navigate(getSettingsAuditHistoryRoute(currentUserType));
        break;
      case Origin.WorkflowsPage:
        navigate(getWorkflowTemplatesRoute(currentUserType));
        break;
      case Origin.DashboardPage:
        navigate(getDashboardRoute(currentUserType));
        break;
      default:
        navigate(getProjectsRoute(currentUserType));
    }
  };

  const getTabsList = (): TabsList => {
    const list = [
      {
        label: "Overview",
        route: getProjectDetailsByUuid(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Details",
        route: getProjectDetailsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Location",
        route: getProjectLocationRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
      ...(projectDetails?.unitMeasurements?.length
        ? [
            {
              label: "Inventory",
              route: getProjectInventoryRoute(
                projectDetails?.uuid || "",
                OrganisationTypeConstants.DEVELOPMENT_MANAGER,
                origin
                  ? {
                      origin,
                    }
                  : undefined
              ),
            },
          ]
        : []),
      {
        label: "Activities",
        route: getProjectActivitiesRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Documents",
        route: getProjectDocumentsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Marketing",
        route: getProjectMarketingSettingsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin ? `origin=${origin}` : undefined
        ),
      },
      {
        label: "Tasks",
        route: getProjectTasksRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Access",
        route: getProjectAccessRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPMENT_MANAGER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
    ];

    return list;
  };

  // The pre-access view only has the public details, so it offers just the tabs that can render from them.
  const getPreAccessTabsList = (): TabsList => {
    // The public details failed to load (ProjectContext has said so), so there is nothing a tab could show.
    if (!publicProjectDetails) return [];

    const { uuid } = publicProjectDetails;
    const routeParams = origin ? { origin } : undefined;

    return [
      {
        label: "Overview",
        route: getProjectDetailsByUuid(uuid, OrganisationTypeConstants.DEVELOPMENT_MANAGER, routeParams),
      },
      // REQ-014: Inventory tab is hidden when the project has no unit measurements (null or empty, e.g. No standard projects).
      ...(publicProjectDetails?.unitMeasurements?.length
        ? [
            {
              label: "Inventory",
              route: getProjectInventoryRoute(uuid, OrganisationTypeConstants.DEVELOPMENT_MANAGER, routeParams),
            },
          ]
        : []),
      {
        label: "Documents",
        route: getProjectDocumentsRoute(uuid, OrganisationTypeConstants.DEVELOPMENT_MANAGER, routeParams),
      },
    ];
  };

  return {
    projectDetails,
    publicProjectDetails,
    handleBackClick,
    tabsList: isPreAccess ? getPreAccessTabsList() : getTabsList(),
    backButtonText,
  };
};
